import { rem } from 'polished';
import { CSSProperties } from 'react';
import { css } from 'styled-components';

import type { Styled } from '../types/common.types';

type SizePropValue = string | number | undefined;

export type BaseSizeProps = {
  boxSizing: CSSProperties['boxSizing'];
  width: SizePropValue;
  maxWidth: SizePropValue;
  minWidth: SizePropValue;
  height: SizePropValue;
  maxHeight: SizePropValue;
  minHeight: SizePropValue;
};

export type SizeProps = Partial<BaseSizeProps>;

export type StyledSizeProps = Styled<SizeProps>;

const sizeValue = (value: SizePropValue): string | undefined => {
  if (typeof value === 'number') {
    return rem(value);
  }

  if (!value) return undefined;

  return value;
};

/**
 * Styles that can map size props prefixed with `$` to an adequate CSS property
 *
 * @example
 * const Test = styled.div<StyledSizeProps>`
 *   ${sizeStyles}
 * `
 */
export const sizeStyles = css<StyledSizeProps>(
  ({
    $width,
    $minWidth,
    $maxWidth,
    $height,
    $minHeight,
    $maxHeight,
    $boxSizing,
  }) => ({
    boxSizing: $boxSizing,
    width: sizeValue($width),
    minWidth: sizeValue($minWidth),
    maxWidth: sizeValue($maxWidth),
    height: sizeValue($height),
    minHeight: sizeValue($minHeight),
    maxHeight: sizeValue($maxHeight),
  }),
);

export const extractSizeProps = <T extends SizeProps>(
  props: T,
): BaseSizeProps => {
  const { width, maxWidth, minWidth, height, maxHeight, minHeight, boxSizing } =
    props;

  const extracted: BaseSizeProps = {
    width,
    maxWidth,
    minWidth,
    height,
    maxHeight,
    minHeight,
    boxSizing,
  };

  return extracted;
};
